import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/** Tangent function parameters: a * tan(b(x - c)) + d */
export interface TangentFnParams {
  /** Amplitude (vertical stretch) */
  a: number;
  /** Frequency (affects period = π/b) */
  b: number;
  /** Phase shift (horizontal translation) */
  c: number;
  /** Vertical shift */
  d: number;
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for tangent functions.
 * All translate behaviors map to c (phase shift) and d (vertical shift).
 *
 * Note: This is named 'tangent-fn' to avoid confusion with the 'tangent' feature
 * which draws tangent lines to curves.
 */
export const TANGENT_FN_META: FunctionMeta<TangentFnParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'tangent',
    'secant',
    'derivative',
    'asymptotes',
    'inflection',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
    'period',
    'phaseShift',
    'domain',
    'range',
  ],
  handleConfigs: {
    translate: { xParam: 'c', yParam: 'd' },
    'translate-x': { xParam: 'c', yParam: 'd' },
    'translate-y': { xParam: 'c', yParam: 'd' },
  },
  defaultParams: {
    a: 1,
    b: 1,
    c: 0,
    d: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [0.1, 5],
    c: [-10, 10],
    d: [-10, 10],
  },
};
